/**
 * Shareable web links for resources (assets published to /resources).
 *
 * Every asset has a slug (auto-generated from its title), so the canonical
 * public link is https://tango-networks.com/resources/<slug>. Legacy links of
 * the form /resources?view=<uuid> still resolve.
 */
export const RESOURCE_SITE_URL = "https://tango-networks.com";

export interface ResourceLinkTarget {
  id: string;
  slug?: string | null;
}

const stripResourcePrefix = (slug: string) =>
  slug
    .trim()
    .replace(/^\/+/, "")
    .replace(/^resources\/+/, "")
    .replace(/\/+$/, "");

/** Collapse duplicate slashes in the pathname only, never in `https://`. */
export function normaliseUrlPathSlashes(value: string): string {
  try {
    const url = new URL(value);
    url.pathname = url.pathname.replace(/\/{2,}/g, "/");
    return url.toString();
  } catch {
    return value;
  }
}

export function publicLinkUrl(pathOrUrl: string): string {
  const input = /^[a-z][a-z\d+.-]*:\/\//i.test(pathOrUrl)
    ? pathOrUrl
    : `/${pathOrUrl.replace(/^\/+/, "")}`;
  return normaliseUrlPathSlashes(new URL(input, RESOURCE_SITE_URL).toString());
}

/** Path portion only, e.g. "/resources/rail-use-case". */
export function resourceLinkPath(target: ResourceLinkTarget): string {
  const slug = target.slug ? stripResourcePrefix(target.slug) : "";
  return slug ? `/resources/${slug}` : `/resources?view=${target.id}`;
}

/** Full public URL suitable for newsletters, emails and sharing. */
export function resourceLinkUrl(
  target: ResourceLinkTarget,
  utm?: { source?: string; medium?: string; campaign?: string },
): string {
  const url = new URL(publicLinkUrl(resourceLinkPath(target)));
  if (utm?.source) url.searchParams.set("utm_source", utm.source);
  if (utm?.medium) url.searchParams.set("utm_medium", utm.medium);
  if (utm?.campaign) url.searchParams.set("utm_campaign", utm.campaign);
  return normaliseUrlPathSlashes(url.toString());
}

/** Copies text to the clipboard, with a fallback for older browsers. */
export async function copyToClipboard(text: string): Promise<boolean> {
  const value = normaliseUrlPathSlashes(text);
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(value);
      return true;
    }
  } catch {
    /* fall through to the legacy path */
  }
  try {
    const el = document.createElement("textarea");
    el.value = value;
    el.setAttribute("readonly", "");
    el.style.position = "fixed";
    el.style.opacity = "0";
    document.body.appendChild(el);
    el.select();
    const ok = document.execCommand("copy");
    document.body.removeChild(el);
    return ok;
  } catch {
    return false;
  }
}
